import { Directive, ElementRef, Renderer2, inject } from '@angular/core';
import {
  COMPOSITION_BUFFER_MODE,
  DefaultValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';

/**
 * The following directive purpose to override the DefaultValueAccessor
 * implementiation by supporting to set-up the cursor position after
 * the super.writeValue(value); was called but before the valueChanges
 * would be fired.
 *
 * Related links:
 * - The problem: https://www.mutuallyhuman.com/blog/the-curious-case-of-cursor-jumping/
 * - Solution: https://stackoverflow.com/questions/43492421/angular-2-cursor-jump-at-the-end-of-textbox-when-changing-data
 * - Documention: https://angular.io/api/forms/DefaultValueAccessor
 * - Source: https://github.com/angular/angular/blob/master/packages/forms/src/directives/default_value_accessor.ts
 */
@Directive({
  selector: '[flControlValueAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ControlValueAccessorDirective,
      multi: true,
    },
  ],
})
export class ControlValueAccessorDirective extends DefaultValueAccessor {
  private elementRef: ElementRef;
  private timeUtils = inject(TimeUtils);

  private offset: number | undefined;
  /** Only refocus if the field was not focused when the caret was set. */
  private restoreFocus = false;

  constructor() {
    const elementRef = inject(ElementRef);
    const renderer = inject(Renderer2, { skipSelf: true });
    const compositionMode =
      inject(COMPOSITION_BUFFER_MODE, { optional: true }) ?? false;

    super(renderer, elementRef, compositionMode);
    this.elementRef = elementRef;
  }

  writeValue(value: any): void {
    super.writeValue(value);

    // Wait for the element to render before restoring the caret.
    this.timeUtils.setTimeout(() => {
      if (!isDefined(this.offset)) {
        return;
      }

      const caretRestore = {
        offset: this.offset,
        restoreFocus: this.restoreFocus,
      };
      // Clear so later writes (e.g. blur validation) don't refocus the field.
      this.offset = undefined;
      this.restoreFocus = false;

      const { nativeElement } = this.elementRef;
      const isFocused =
        nativeElement.ownerDocument.activeElement === nativeElement;

      if (!isFocused && !caretRestore.restoreFocus) {
        return;
      }

      if (!isFocused) {
        nativeElement.focus();
      }

      nativeElement.setSelectionRange(caretRestore.offset, caretRestore.offset);
    });
  }

  setCursorPosition(offset?: number): void {
    this.offset = offset;

    const { nativeElement } = this.elementRef;
    this.restoreFocus =
      offset !== undefined &&
      nativeElement.ownerDocument.activeElement !== nativeElement;
  }

  getCursorSelectionPosition(): {
    selectionStart: number;
    selectionEnd: number;
  } {
    const { nativeElement } = this.elementRef;

    return {
      selectionStart: nativeElement.selectionStart,
      selectionEnd: nativeElement.selectionEnd,
    };
  }
}
